import { addDays, differenceInDays, formatCivilDate, parseCivilDate, } from './civil-date.js';
export const ATTENTION_KIND = {
    CHARGE_SOON: 'charge_soon',
    PRICE_CHANGED: 'price_changed',
    NEW_SUBSCRIPTION: 'new_subscription',
    MISSED_CHARGE: 'missed_charge',
    YEARLY_SOON: 'yearly_soon',
    PAY_YOURSELF: 'pay_yourself',
};
const CHARGE_SOON_DAYS = 3;
export const YEARLY_SOON_DAYS = 30;
export const MISSED_CHARGE_DAYS = 14;
const PRICE_CHANGE_DAYS = 60;
const ID_SEPARATOR = ':';
const URGENCY = {
    NOW: 0,
    SOON: 1,
    NOTICE: 2,
};
const attentionIdOf = (kind, subject) => [kind, ...subject].join(ID_SEPARATOR);
export const amountToCover = (amount) => amount.kind === 'fixed'
    ? { amountMinor: amount.amountMinor, currency: amount.currency }
    : { amountMinor: amount.maxMinor, currency: amount.currency };
const isBalanceShort = (balanceMinor, neededMinor) => balanceMinor < neededMinor;
export const firstWatchedDay = (today, days) => formatCivilDate(addDays(today, -days));
export const lastWatchedDay = (today, days) => formatCivilDate(addDays(today, days));
const isWithin = (date, from, to) => date >= from && date <= to;
const daysUntil = (today, date) => differenceInDays(today, parseCivilDate(date));
const daysSince = (date, today) => differenceInDays(parseCivilDate(date), today);
const isPayYourself = (period, today) => {
    const isManual = period.paymentKind === 'manual';
    const isDue = isManual && period.dueDate <= formatCivilDate(today);
    return isDue && !period.hasBankCharge;
};
const isYearlySoon = (period, today) => {
    const isYearly = period.intervalUnit === 'year';
    const isClose = isYearly &&
        isWithin(period.dueDate, formatCivilDate(today), lastWatchedDay(today, YEARLY_SOON_DAYS));
    return isClose && !period.hasBankCharge;
};
const isChargeSoon = (period, today) => {
    const isAutoDebit = period.paymentKind === 'auto_debit';
    const isClose = isAutoDebit &&
        isWithin(period.dueDate, formatCivilDate(today), lastWatchedDay(today, CHARGE_SOON_DAYS));
    return isClose && !period.hasBankCharge;
};
const yearlySoonOf = (period, today, isLowBalance) => ({
    id: attentionIdOf(ATTENTION_KIND.YEARLY_SOON, [period.occurrenceId]),
    kind: ATTENTION_KIND.YEARLY_SOON,
    dueDate: period.dueDate,
    paymentId: period.paymentId,
    occurrenceId: period.occurrenceId,
    name: period.name,
    daysLeft: daysUntil(today, period.dueDate),
    amount: period.amount,
    cancelUrl: period.cancelUrl,
    isLowBalance,
});
const chargeSoonOf = (period, today, isLowBalance) => ({
    id: attentionIdOf(ATTENTION_KIND.CHARGE_SOON, [period.occurrenceId]),
    kind: ATTENTION_KIND.CHARGE_SOON,
    dueDate: period.dueDate,
    paymentId: period.paymentId,
    occurrenceId: period.occurrenceId,
    name: period.name,
    daysLeft: daysUntil(today, period.dueDate),
    amount: period.amount,
    isLowBalance,
});
const upcomingItemOf = (period, context) => {
    const isLowBalance = context.lowBalanceIds.has(period.occurrenceId);
    const isUnconfirmed = context.unconfirmedPaymentIds.has(period.paymentId);
    const isYearlyAsked = !isUnconfirmed && isYearlySoon(period, context.today);
    const yearly = isYearlyAsked ? yearlySoonOf(period, context.today, isLowBalance) : null;
    const isYearlyOpen = yearly !== null && !context.dismissedIds.has(yearly.id);
    if (isYearlyOpen) {
        return [yearly];
    }
    const isChargeClose = isChargeSoon(period, context.today);
    const isChargeWorthShowing = isChargeClose && (!isUnconfirmed || isLowBalance);
    return isChargeWorthShowing ? [chargeSoonOf(period, context.today, isLowBalance)] : [];
};
const byDueDate = (left, right) => {
    if (left.dueDate === right.dueDate) {
        return left.occurrenceId < right.occurrenceId ? -1 : 1;
    }
    return left.dueDate < right.dueDate ? -1 : 1;
};
const tallyCardFunds = (tally, period) => {
    const { funds } = period;
    if (funds === null) {
        return tally;
    }
    const neededMinor = (tally.neededByCard.get(funds.cardId) ?? 0) + funds.neededMinor;
    const isShort = isBalanceShort(funds.balanceMinor, neededMinor);
    return {
        neededByCard: new Map(tally.neededByCard).set(funds.cardId, neededMinor),
        shortIds: isShort ? [...tally.shortIds, period.occurrenceId] : tally.shortIds,
    };
};
const lowBalanceIdsOf = (periods, today) => {
    const tally = periods
        .filter((period) => isChargeSoon(period, today))
        .sort(byDueDate)
        .reduce(tallyCardFunds, { neededByCard: new Map(), shortIds: [] });
    return new Set(tally.shortIds);
};
const payYourselfItemsOf = (periods, today) => {
    const unpaid = periods.filter((period) => isPayYourself(period, today)).sort(byDueDate);
    const byPayment = unpaid.reduce((groups, period) => new Map(groups).set(period.paymentId, [...(groups.get(period.paymentId) ?? []), period]), new Map());
    return [...byPayment.values()].flatMap((group) => {
        const oldest = group[0];
        if (oldest === undefined) {
            return [];
        }
        return [
            {
                id: attentionIdOf(ATTENTION_KIND.PAY_YOURSELF, [oldest.occurrenceId]),
                kind: ATTENTION_KIND.PAY_YOURSELF,
                dueDate: oldest.dueDate,
                paymentId: oldest.paymentId,
                occurrenceId: oldest.occurrenceId,
                name: oldest.name,
                daysOverdue: daysSince(oldest.dueDate, today),
                unpaidCount: group.length,
                amount: oldest.amount,
            },
        ];
    });
};
const periodItemsOf = (input, unconfirmedPaymentIds) => {
    const payYourself = payYourselfItemsOf(input.periods, input.today);
    const payYourselfIds = new Set(input.periods
        .filter((period) => isPayYourself(period, input.today))
        .map((period) => period.occurrenceId));
    const context = {
        today: input.today,
        dismissedIds: input.dismissedIds,
        lowBalanceIds: lowBalanceIdsOf(input.periods, input.today),
        unconfirmedPaymentIds,
    };
    const upcoming = input.periods
        .filter((period) => !payYourselfIds.has(period.occurrenceId))
        .flatMap((period) => upcomingItemOf(period, context));
    return [...payYourself, ...upcoming];
};
const missedItemsOf = (input) => {
    const from = firstWatchedDay(input.today, MISSED_CHARGE_DAYS);
    const to = formatCivilDate(input.today);
    return input.missed
        .filter((period) => isWithin(period.dueDate, from, to))
        .map((period) => ({
        id: attentionIdOf(ATTENTION_KIND.MISSED_CHARGE, [period.occurrenceId]),
        kind: ATTENTION_KIND.MISSED_CHARGE,
        dueDate: period.dueDate,
        paymentId: period.paymentId,
        occurrenceId: period.occurrenceId,
        name: period.name,
        amount: period.amount,
        isStopped: period.isStopped,
    }));
};
const newSubscriptionItemsOf = (input) => input.subscriptions
    .filter((subscription) => subscription.isNew && subscription.isRunning)
    .map((subscription) => ({
    id: attentionIdOf(ATTENTION_KIND.NEW_SUBSCRIPTION, [subscription.paymentId]),
    kind: ATTENTION_KIND.NEW_SUBSCRIPTION,
    dueDate: subscription.nextDueDate,
    paymentId: subscription.paymentId,
    name: subscription.name,
    amount: subscription.amount,
    intervalUnit: subscription.intervalUnit,
}));
const priceChangedItemsOf = (input) => {
    const from = firstWatchedDay(input.today, PRICE_CHANGE_DAYS);
    const to = formatCivilDate(input.today);
    return input.subscriptions.flatMap((subscription) => {
        const change = subscription.priceChange;
        const isRecent = change !== null && isWithin(change.changedOn, from, to);
        if (change === null || !isRecent || !subscription.isRunning) {
            return [];
        }
        return [
            {
                id: attentionIdOf(ATTENTION_KIND.PRICE_CHANGED, [
                    subscription.paymentId,
                    change.changedOn,
                    String(change.amountMinor),
                ]),
                kind: ATTENTION_KIND.PRICE_CHANGED,
                dueDate: null,
                paymentId: subscription.paymentId,
                name: subscription.name,
                previousAmountMinor: change.previousAmountMinor,
                amountMinor: change.amountMinor,
                currency: change.currency,
                changedOn: change.changedOn,
            },
        ];
    });
};
const urgencyOf = (item) => {
    switch (item.kind) {
        case ATTENTION_KIND.PAY_YOURSELF:
            return URGENCY.NOW;
        case ATTENTION_KIND.CHARGE_SOON:
        case ATTENTION_KIND.YEARLY_SOON:
            return item.isLowBalance ? URGENCY.NOW : URGENCY.SOON;
        case ATTENTION_KIND.MISSED_CHARGE:
        case ATTENTION_KIND.NEW_SUBSCRIPTION:
        case ATTENTION_KIND.PRICE_CHANGED:
            return URGENCY.NOTICE;
    }
};
const sortDateOf = (item) => {
    switch (item.kind) {
        case ATTENTION_KIND.PRICE_CHANGED:
            return item.changedOn;
        default:
            return item.dueDate;
    }
};
const compareDates = (left, right) => {
    if (left === right) {
        return 0;
    }
    if (left === null) {
        return 1;
    }
    if (right === null) {
        return -1;
    }
    return left < right ? -1 : 1;
};
const compareAttentionItems = (left, right) => {
    const byUrgency = urgencyOf(left) - urgencyOf(right);
    if (byUrgency !== 0) {
        return byUrgency;
    }
    const byDate = compareDates(sortDateOf(left), sortDateOf(right));
    if (byDate !== 0) {
        return byDate;
    }
    return left.id < right.id ? -1 : 1;
};
export const attentionItemsOf = (input) => {
    const newSubscriptions = newSubscriptionItemsOf(input).filter((item) => !input.dismissedIds.has(item.id));
    const unconfirmedPaymentIds = new Set(newSubscriptions.map((item) => item.paymentId));
    return [
        ...periodItemsOf(input, unconfirmedPaymentIds),
        ...missedItemsOf(input),
        ...newSubscriptions,
        ...priceChangedItemsOf(input),
    ]
        .filter((item) => !input.dismissedIds.has(item.id))
        .sort(compareAttentionItems);
};
//# sourceMappingURL=attention.js.map